<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {computed, inject, reactive, ref, watch} from "vue";
import {loyaltyCardApi} from "@/container";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {cacheKey} from "@/lib/cache";
import debounce from "lodash.debounce";
import {CircleX, RotateCcw} from "@lucide/vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import dateHelper from "../../core/helpers/dateHelper";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {pluralize} from "@/core/filters/Pluralize";
import {LoyaltyCardProduct} from "@/loyalty-card/interfaces/LoyaltyCard";
import {Badge} from "@/core/components/ui/badge";
import {AutoCompleteSearchbar} from "@/auto-complete-searchbar/components/searchbar";
import FadeTransition from "@/core/transitions/Fade.vue";
import {
    ProductSearchQueryParams,
    SearchBarAutoCompleteItem
} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import SearchBarAutoCompleteService from "@/auto-complete-searchbar/services/SearchBarAutoCompleteService";
import LoyaltyCardAddProductButton from "@/loyalty-card/components/LoyaltyCardAddProductButton.vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";

const props = defineProps<{
    loyaltyCardId: string;
}>();

const sort = ref([]);
const selected = ref<LoyaltyCardProduct[]>([]);
const searchItems = ref<SearchBarAutoCompleteItem[]>([]);
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

const updateSearchItem = debounce((items: SearchBarAutoCompleteItem[]) => {
    searchParams.value = SearchBarAutoCompleteService.transformToQueryParams(items);
}, 500);

const searchParams = ref<ProductSearchQueryParams>({});
const params = reactive<{
    size: number;
    page: number;
    sort: undefined | { key: string; order: 'asc' | 'desc' | undefined },
    type: 'all' | 'included' | 'excluded'
}>({
    size: 20,
    page: 1,
    sort: undefined,
    type: 'all'
});

const {data, isValidating, mutate} = useSwr(
    computed(() => cacheKey(`loyalty-cards/${props.loyaltyCardId}/products`, {...params, ...searchParams.value})),
    () => loyaltyCardApi().products(props.loyaltyCardId, {...params, ...searchParams.value}),
);

watch(sort, () => {
    params.sort = sort.value.length > 0 ? sort.value[0] : undefined;
});

watch(searchItems, updateSearchItem);

/**
 * Exclude the products from the selection.
 */
const excludeFromSelection = () => {
    if (selected.value.length === 0) {
        return;
    }

    openConfirmationModal(
        "Exclure du ciblage",
        `Êtes-vous sûr de vouloir exclure ${removableProductsFromSelection.value.length} ${pluralize('produit', removableProductsFromSelection.value.length)} du ciblage ?`,
        async () => {
            const ids = removableProductsFromSelection.value.map((item) => item.id);
            await loyaltyCardApi().excludeProducts(props.loyaltyCardId, ids);
            selected.value = [];
            mutate();

            return Promise.resolve();
        }
    );
};

/**
 * Exclude a product from the targeting.
 *
 * @param productId
 */
const exclude = (productId: number) => {
    openConfirmationModal(
        "Exclure du ciblage",
        `Êtes-vous sûr de vouloir exclure ce produit ?`,
        async () => {
            await loyaltyCardApi().excludeProducts(props.loyaltyCardId, [productId]);
            selected.value = [];
            mutate();

            return Promise.resolve();
        }
    );
};

const deleteHealthcenterProduct = (healthcenterProductId: number) => {
    openConfirmationModal(
        "Supprimer le produit",
        `Êtes-vous sûr de vouloir supprimer la configuration de ce produit ?`,
        async () => {
            await loyaltyCardApi().deleteHealthcenterProduct(props.loyaltyCardId, healthcenterProductId);
            selected.value = [];
            mutate();

            return Promise.resolve();
        }
    );
};

const headers = [
    {
        title: "Nom",
        key: "name"
    },
    {
        title: "CIP",
        key: "cip",
        sortable: false
    },
    {
        title: "",
        key: "actions",
        sortable: false,
        width: '150px',
        align: 'end' as ('start' | 'end')
    }
];

/**
 * Get the selection products that can be removed from targeting.
 */
const removableProductsFromSelection = computed(() => {
    return selected.value.filter((item) => {
        return !item.healthcenter_product || item.healthcenter_product.is_included;
    });
});
</script>

<template>
  <BaseContainer class="p-6">
    <BaseCard class="overflow-visible">
      <BaseCardHeader>
        <div class="flex flex-1 items-center justify-between h-8">
          <div class="flex gap-4 items-center">
            Produits éligibles
            <LoyaltyCardAddProductButton :loyalty-card-id="loyaltyCardId" @included="mutate()" />
          </div>
          <div class="flex flex-col gap-2 items-end">
            <BaseSelect
              v-model:model-value="params.type"
              trigger-class="w-[180px]"
              :options="[
                {label: 'Tous les produits', value: 'all'},
                {label: 'Produits inclus', value: 'included'},
                {label: 'Produits exclus', value: 'excluded'}]"
            />
            <fade-transition>
              <div v-if="removableProductsFromSelection.length">
                <BaseButton primary density="comfortable" @click="excludeFromSelection">
                  Exclure {{ removableProductsFromSelection.length }} {{ pluralize('produit', removableProductsFromSelection.length) }} du
                  ciblage
                </BaseButton>
              </div>
            </fade-transition>
          </div>
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <div class="flex w-full">
          <AutoCompleteSearchbar v-model:selected-items="searchItems" class="flex-1" />
        </div>
        <v-data-table-server
          v-model:items-per-page="params.size"
          v-model:page="params.page"
          v-model:sort-by="sort"
          v-model="selected"
          show-select
          return-object
          :headers="headers"
          :loading="isValidating"
          :items="data?.data ?? []"
          :items-length="data?.meta.total ?? 0"
          :items-per-page-options="[10, 20, 50, 100]"
          class="product-table"
        >
          <template #[`item.name`]="{item}">
            <div class="flex gap-2 items-center">
              <p>{{ item.name }}</p>
              <TooltipProvider v-if="item.healthcenter_product">
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="primary">
                      {{ item.healthcenter_product.is_included ? 'Inclus' : 'Exclus' }}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent class="text-sm" avoid-collisions>
                    <p>
                      Vous avez {{ item.healthcenter_product.is_included ? 'inclus' : 'exclus' }} ce produit le {{
                        dateHelper.shortFormat(item.healthcenter_product.updated_at)
                      }}{{ !item.healthcenter_product.is_included ? ', aucun point ne sera crédité pour ce produit.' : '' }}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </template>
          <template #[`item.actions`]="{item}">
            <div class="action-buttons">
              <BaseButton
                v-if="!item.healthcenter_product"
                v-tooltip="'Exclure le produit du ciblage'"
                primary
                icon
                density="compact"
                @click="exclude(item.id)"
              >
                <CircleX class="w-4 h-4" />
              </BaseButton>
              <BaseButton
                v-else
                v-tooltip="item.healthcenter_product.is_included ? 'Exclure le produit du ciblage' : 'Inclure le produit dans le ciblage'"
                primary
                icon
                density="compact"
                @click="deleteHealthcenterProduct(item.healthcenter_product.id)"
              >
                <RotateCcw class="w-4 h-4" />
              </BaseButton>
            </div>
          </template>
        </v-data-table-server>
      </BaseCardBody>
    </BaseCard>
  </BaseContainer>
</template>

<style scoped>
.product-table :deep(tr) {
    position: relative;
}

.product-table :deep(tr:hover) {
    background-color: rgba(0, 0, 0, 0.04);
}

.product-table :deep(tr) .action-buttons {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.product-table :deep(tr:hover) .action-buttons {
    opacity: 1;
}
</style>
